import React, { useEffect, useRef, useState } from "react"
import clsx from "clsx"

import { moment, thousandSeparator } from "utils"
import { Button, Card, PageLoader, Tooltip } from "components"
import { getContract, getContractStats } from "services"
import classes from "./SpCurrentPlan.module.scss"

const SpCurrentPlan = (props) => {
  const {
    setPage,
    setSelectedPlan,
    contractDetails,
    setContractDetails,
    contractStats,
    setContractStats,
    setExtendCurrentContract,
    currentContractSubscriptionPlan,
    setCurrentContractSubscriptionPlan
  } = props

  const [loading, setLoading] = useState(false)

  const unmount = useRef(false)

  const isCurrentContractSubscriptionPlanChanged = () => {
    if (currentContractSubscriptionPlan === null) return false

    const result =
      currentContractSubscriptionPlan.term === contractDetails.term &&
      currentContractSubscriptionPlan.salePrice === contractDetails.salePrice &&
      currentContractSubscriptionPlan.maximumProductNumber === contractDetails.maximumProductNumber &&
      currentContractSubscriptionPlan.maximumBranchNumber === contractDetails.maximumBranchNumber &&
      currentContractSubscriptionPlan.maximumAccessUserNumber === contractDetails.maximumAccessUserNumber &&
      currentContractSubscriptionPlan.maximumStorageSpaceInByte === contractDetails.maximumStorageSpaceInByte

    return !result
  }

  const isMaxedOut = (max, value) => (
    max !== 0 && value >= max
  )

  const handleExtendSubscription = async () => {
    setSelectedPlan({ ...currentContractSubscriptionPlan })

    setPage("checkout")

    setExtendCurrentContract(true)

    setLoading(true)
  }

  const handleChangeSubscription = () => {
    setPage("plans")

    setExtendCurrentContract(false)
  }

  const getContractDetails = async () => {
    if (contractDetails !== null) return

    try {
      const response = await getContract()

      if (unmount.current) return

      const { subscriptionPlan, ...contract } = response

      setContractDetails({ ...contract })

      setCurrentContractSubscriptionPlan({ ...subscriptionPlan[0] })
    } catch (err) {
      console.log(err)
    }
  }

  const getStats = async () => {
    if (contractStats !== null) return

    try {
      const response = await getContractStats()

      if (unmount.current) return

      setContractStats(response)
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    unmount.current = false

    getContractDetails()

    getStats()

    return () => {
      unmount.current = true
    }
  }, [])

  const pageLoading =
    contractDetails === null ||
    contractStats === null ||
    currentContractSubscriptionPlan === null

  if (pageLoading) return <PageLoader />

  const currentContractSubscriptionPlanChanged =
    isCurrentContractSubscriptionPlanChanged()

  const getContractCostPerMinute = () => {
    const minutesInOneMonth = 30 * 24 * 60

    const costPerMinute =
      Math.round(
        contractDetails.salePrice / (contractDetails.term * minutesInOneMonth)
      )

    return thousandSeparator(costPerMinute)
  }

  return (
    <Card className={classes["container"]}>
      <div className={classes["item"]}>
        <span>
          نام اشتراک:
        </span>
        <b className={classes["service-name"]}>
          {currentContractSubscriptionPlan.name || "اشتراک ویژه"}
        </b>
      </div>

      <div className={classes["item"]}>
        <span>
          وضعیت سرویس:
        </span>
        <b
          className={clsx(
            classes["status"],
            contractDetails.contractStatus !== "fullActive" && classes["disabled"]
          )}
        >
          {contractDetails.contractStatus === "fullActive" ? "فعال" : "غیر فعال"}
        </b>
      </div>

      <div className={classes["item"]}>
        <span>
          هزینه بر دقیقه:
        </span>
        <b className={classes["number"]}>
          {getContractCostPerMinute()}
        </b>
      </div>

      <div className={classes["item"]}>
        <span>
          تاریخ شروع:
        </span>
        <b className={classes["date"]}>
          {moment(contractDetails.startDate).format("jYYYY/jMM/jDD")}
        </b>
      </div>

      <div className={classes["item"]}>
        <span>
          تاریخ انقضا:
        </span>
        <b className={classes["date"]}>
          {moment(contractDetails.expiryDate).format("jYYYY/jMM/jDD")}
        </b>
      </div>

      <div className={classes["item"]}>
        <span>
          پیش پرداخت:
        </span>
        <b className={classes["number"]}>
          {thousandSeparator(contractDetails.salePrice)}
        </b>
      </div>

      <div className={classes["item"]}>
        <span>
          درصد کمیسیون به ازای هر فروش:
        </span>
        <b className={classes["number"]}>
          %{Math.round(contractDetails.percentagePerOrderValue * 100)}
        </b>
      </div>

      <div className={classes["item"]}>
        <span>
          سقف روزانه کمیسیون سفارشات:
        </span>
        <b className={classes["number"]}>
          {thousandSeparator(contractDetails.dailyCap)}
        </b>
      </div>

      {
        contractDetails.haveDebtThreshold &&
        <div className={classes["item"]}>
          <span>
            میزان سقف بدهی:
          </span>
          <b className={classes["number"]}>
            {thousandSeparator(contractDetails.debtThreshold)}
          </b>
        </div>
      }

      <div className={classes["item"]}>
        <span>
          تعداد محصولات:
        </span>
        <span>
          <b
            className={clsx(
              classes["stats"],
              isMaxedOut(contractDetails.maximumProductNumber, contractStats.numberOfFoods) && classes["danger"]
            )}
          >
            {contractStats.numberOfFoods}
          </b>

          {contractDetails.maximumProductNumber !== 0
            ? ` / ${contractDetails.maximumProductNumber}`
            : ""
          }
        </span>
      </div>

      <div className={classes["item"]}>
        <span>
          تعداد شعبات:
        </span>

        <span>
          <b
            className={clsx(
              classes["stats"],
              isMaxedOut(contractDetails.maximumBranchNumber, contractStats.numberOfBranches) && classes["danger"]
            )}
          >
            {contractStats.numberOfBranches}
          </b>
          {contractDetails.maximumBranchNumber !== 0
            ? ` / ${contractDetails.maximumBranchNumber}`
            : ""
          }
        </span>
      </div>

      <div className={classes["item"]}>
        <span>
          تعداد پرسنل:
        </span>

        <span>
          <b
            className={clsx(
              classes["stats"],
              isMaxedOut(contractDetails.maximumAccessUserNumber, contractStats.numberOfAccesses) && classes["danger"]
            )}
          >
            {contractStats.numberOfAccesses}
          </b>
          {contractDetails.maximumAccessUserNumber !== 0
            ? ` / ${contractDetails.maximumAccessUserNumber}`
            : ""
          }
        </span>
      </div>

      <div className={classes["item"]}>
        <span>
          فضای ذخیره سازی:
        </span>{" "}

        <b>
          {Number(contractDetails.maximumStorageSpaceInByte / Math.pow(10, 9))} گیگابایت
        </b>
      </div>

      <div className={classes["footer"]}>
        <Tooltip
          title={
            currentContractSubscriptionPlanChanged && !contractDetails.isPremiumContract
              ? "به دلیل تغییرات در طرح شما این اشتراک قابل تمدید نمی باشد لطفا بعد از انقضای اشتراک خود طرح خود را تغییر دهید"
              : ""
          }
        >
          <div>
            <Button
              color="info"
              loading={loading}
              disabled={currentContractSubscriptionPlanChanged || contractDetails.isPremiumContract}
              className={classes["button"]}
              onClick={handleExtendSubscription}
            >
              تمدید اشتراک
            </Button>
          </div>
        </Tooltip>

        <Button
          color="danger"
          disabled={loading || contractDetails.isPremiumContract}
          onClick={handleChangeSubscription}
        >
          تغییر اشتراک
        </Button>
      </div>
    </Card>
  )
}

export default SpCurrentPlan